import React, { useState, useEffect } from 'react';
import './PokerClock.css';
import TimerDisplay from '../TimerDisplay/TimerDisplay';
import BlindDisplay from '../BlindDisplay/BlindDisplay';
import ControlButtons from '../ControlButtons/ControlButtons';
import Settings from '../Settings/Settings';
import { useTheme } from '../../contexts/ThemeContext';
import { useTimer } from '../../hooks/useTimer';
import { useSound } from '../../hooks/useSound';
import defaultBlinds from '../../data/defaultBlinds';
import { getFromStorage, saveToStorage } from '../../utils/storage';
import { appConfig } from '../../config/appConfig';

const PokerClock = () => {
    const { darkMode } = useTheme();
    const [showSettings, setShowSettings] = useState(false);
    const [level, setLevel] = useState(1);
    const [blinds, setBlinds] = useState(defaultBlinds[0]);
    const [blindLevels, setBlindLevels] = useState(() => {
        return getFromStorage('blindLevels', defaultBlinds);
    });

    const { soundEnabled, toggleSound, muted, toggleMute } = useSound();

    // Sprawdzenie, czy aplikacja jest uruchomiona w środowisku DEV
    const isDev = process.env.REACT_APP_ENV === 'development';

    // Obsługa zakończenia czasu - przejście do następnego poziomu
    const handleTimeEnd = () => {
        goToNextLevel();
    };

    const {
        time,
        isRunning,
        customTime,
        startTimer,
        pauseTimer,
        resetTimer,
        handleTimeChange
    } = useTimer(1, handleTimeEnd); // Zmieniono domyślny czas na 1 minutę

    // Nowa funkcja do resetowania i natychmiastowego uruchomienia
    const resetAndStartTimer = () => {
        resetTimer();
        startTimer();
    };

    // Zapisanie poziomów blindów do localStorage przy zmianie
    useEffect(() => {
        saveToStorage('blindLevels', blindLevels);
    }, [blindLevels]);

    // Przejście do następnego poziomu
    const goToNextLevel = () => {
        if (level < blindLevels.length) {
            setLevel(prevLevel => {
                const newLevel = prevLevel + 1;
                setBlinds(blindLevels[newLevel - 1]);
                return newLevel;
            });
            resetTimer();
        }
    };

    // Przejście do poprzedniego poziomu
    const goToPrevLevel = () => {
        if (level > 1) {
            setLevel(prevLevel => {
                const newLevel = prevLevel - 1;
                setBlinds(blindLevels[newLevel - 1]);
                return newLevel;
            });
            resetTimer();
        }
    };

    // Dodawanie własnego poziomu blindów
    const addCustomBlindLevel = (small, big) => {
        if (small > 0 && big > 0) {
            setBlindLevels(prev => [...prev, { small, big }]);
        }
    };

    // Usuwanie poziomu blindów
    const removeBlindLevel = (index) => {
        if (blindLevels.length > 1) {
            setBlindLevels(prev => prev.filter((_, i) => i !== index));

            // Jeśli usuwamy aktualny poziom lub poziom niższy, musimy zaktualizować wskaźniki
            if (index < level - 1) {
                setLevel(prev => prev - 1);
            } else if (index === level - 1) {
                setLevel(1);
                setBlinds(blindLevels[0]);
            }
        }
    };

    // Edycja poziomu blindów
    const editBlindLevel = (index, small, big) => {
        if (small > 0 && big > 0) {
            setBlindLevels(prev =>
                prev.map((blind, i) => i === index ? { small, big } : blind)
            );

            // Aktualizacja aktualnych blindów, jeśli edytujemy aktualny poziom
            if (index === level - 1) {
                setBlinds({ small, big });
            }
        }
    };

    // Klasy dla ciemnego/jasnego motywu
    const themeClass = darkMode ? 'dark-theme' : 'light-theme';

    return (
        <div className={`poker-clock ${themeClass}`}>
            <h1 className="poker-clock-title">Poker Timer {isDev && <span className="dev-badge">DEV</span>}</h1>

            <div className="level-info">Poziom: {level}</div>

            <BlindDisplay small={blinds.small} big={blinds.big} />

            <TimerDisplay time={time} />

            <ControlButtons
                isRunning={isRunning}
                onStart={startTimer}
                onPause={pauseTimer}
                onReset={resetTimer}
                onResetAndStart={resetAndStartTimer}
                onPrev={goToPrevLevel}
                onNext={goToNextLevel}
                onToggleSettings={() => setShowSettings(!showSettings)}
            />

            {showSettings && (
                <Settings
                    customTime={customTime}
                    onTimeChange={handleTimeChange}
                    soundEnabled={soundEnabled}
                    onToggleSound={toggleSound}
                    muted={muted}
                    onToggleMute={toggleMute}
                    blindLevels={blindLevels}
                    onAddBlindLevel={addCustomBlindLevel}
                    onRemoveBlindLevel={removeBlindLevel}
                    onEditBlindLevel={editBlindLevel}
                />
            )}

            <div className="version-footer">
                <span className="version-text">v{appConfig.version}</span>
            </div>
        </div>
    );
};

export default PokerClock;